// The server's own sentence for a refusal it wrote for people to read.
//
// api-new rethrows axios's error.response, so a failed call surfaces as
// { status, data: <response body> }. A body carrying a `code` (rapid-api's
// optional error code, e.g. 409 organization.last_owner) has a message meant
// for the person, and so does one carrying a reason in WRITTEN_REASONS;
// anything else (old servers, raw database errors) gets the page's fallback.
const bodyOf = error => (error && error.data && typeof error.data === 'object' ? error.data : {})

// Refusals that carry a `reason` rather than a `code` but whose message is
// written for the person all the same: rapid-api-auth's Require SSO guard
// (409 require_sso_on, "Turn off Require SSO for acme.org first.") on any
// action that would leave a locked domain with no connection routing it.
export const WRITTEN_REASONS = ['require_sso_on']

// Whether a body's message was written for people to read.
const isWritten = body => Boolean(body.code) || WRITTEN_REASONS.includes(body.reason)

export const LAST_OWNER_CODES = ['organization.last_owner', 'account.last_owner']

export const isCode = (error, code) => bodyOf(error).code === code

export const isLastOwner = error => LAST_OWNER_CODES.includes(bodyOf(error).code)

// The organizations a last-owner refusal names ({id, name}), when it names any.
export const refusedOrganizations = error => (Array.isArray(bodyOf(error).organizations) ? bodyOf(error).organizations : [])

export const serverMessage = (error, fallback) => {
  const body = bodyOf(error)
  return isWritten(body) && typeof body.message === 'string' && body.message ? body.message : fallback
}

// The organization gate's refusal (core orggate, delete organization spec
// §2.3): this session's organization has been switched off.
export const isOrganizationUnavailable = error =>
  !!error && error.status === 401 && bodyOf(error).code === 'organization_unavailable'

// The message for a recycle-bin action (restore/purge/delete) that failed.
// A 404 means the row is already gone -- most likely someone else restored
// or purged it first -- so that gets the plain, specific `alreadyRemoved`
// sentence regardless of what the body says. Anything else shows the
// server's own sentence ONLY when the body is written (a `code`, or a reason
// in WRITTEN_REASONS) -- same rule as serverMessage, and for the same
// reason: an uncoded body (old servers, raw database errors) was shown to a
// user once, a Postgres foreign-key error
// (commit 14ae449d, pinned by page-last-owner.test.js), and must never be
// again. A coded refusal still isn't mislabeled as "already removed"; an
// uncoded one gets a generic retry message, never the raw text.
export const recycleErrorMessage = (error, alreadyRemoved) => {
  if (error && error.status === 404) return alreadyRemoved

  const body = bodyOf(error)
  if (isWritten(body) && typeof body.message === 'string' && body.message) return body.message

  return 'Something went wrong. Please try again.'
}
